import { groupBy, isEmpty, isNaN } from 'lodash-es';
import moment from 'moment';
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { TooltipProps } from 'recharts';

import { IDashboardHomeCardMetricKey } from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import {
  CHART_COLORS,
  formatTooltipDate,
  formatTooltipValue,
  IDashboardChartMetric,
} from '~/src/dash/components/charts/common';
import { LoadingChart } from '~/src/dash/components/charts/LoadingChart';
import { Col, Cols, fontSizing, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { calculateTrend, displayArrow, getTrendColors } from '~/src/dash/utils/trends';
import { useMobileState, useMobileStore } from '~/src/dash/views/mobile/contexts';

const generateCompareChartTickArray = (data: Array<Record<string, any>>) => {
  const max = data.length - 1;
  if (max < 1) {
    return [];
  }
  // if week, 12 hour, or 15 minutes, create 3 ticks, else create 5
  if (max === 6 || max === 11 || max === 14) {
    return [data[0].main.Date, data[Math.trunc(max / 2)].main.Date, data[max].main.Date];
  } else {
    return [
      data[0].main.Date,
      data[Math.trunc(max / 4)].main.Date,
      data[Math.trunc(max / 2)].main.Date,
      data[Math.trunc((3 * max) / 4)].main.Date,
      data[max].main.Date,
    ];
  }
};

const renderItems = (
  metrics: IDashboardChartMetric[],
  numItems: number,
  isCompareEnabled?: boolean,
) => {
  const items: any[] = [];
  metrics.forEach((metric) => {
    const colorIndex = metric.chartColorIndex || 0;
    if (metric.chartType === 'bar') {
      items.push(
        <Bar
          name={metric.label}
          dataKey={`main.${metric.key}`}
          barSize={8}
          fill={CHART_COLORS[colorIndex][0]}
          stackId='main'
        />,
      );
      if (isCompareEnabled) {
        items.push(
          <Bar
            name={metric.label}
            dataKey={`compare.${metric.key}`}
            barSize={8}
            fill={CHART_COLORS[colorIndex][1]}
            stackId='compare'
          />,
        );
      }
    }
    if (metric.chartType === 'line') {
      if (isCompareEnabled) {
        items.push(
          <Line
            name={metric.label}
            type='monotone'
            dataKey={`compare.${metric.key}`}
            stroke={CHART_COLORS[colorIndex][1]}
            fill={CHART_COLORS[colorIndex][1]}
            strokeWidth={2}
            dot={false}
          />,
        );
      }
      items.push(
        <Line
          name={metric.label}
          type='monotone'
          dataKey={`main.${metric.key}`}
          stroke={CHART_COLORS[colorIndex][0]}
          fill={CHART_COLORS[colorIndex][0]}
          strokeWidth={2}
          activeDot={{ r: 8 }}
          dot={numItems <= 15}
        />,
      );
    }
  });
  return items;
};

interface ITrendDisplayProps {
  value: number | null;
  isInverseTrend?: boolean;
}

const TrendDisplay = ({ value, isInverseTrend }: ITrendDisplayProps) => {
  if (!value) {
    return <Text extraSmall bold color={publicaColors.gray} disableSelect></Text>;
  }
  const colors = getTrendColors(value, isInverseTrend);
  return (
    <Cols
      gap='4px'
      style={{
        alignItems: 'center',
        backgroundColor: colors.background,
        borderRadius: '4px',
        padding: '1px 6px 0px 4px',
      }}
    >
      <Col displayFlex width={16}>
        {displayArrow(value, colors.text)}
      </Col>
      <Col>
        <Text extraSmall bold color={colors.text} disableSelect>
          {`${Math.abs(value).toFixed(2)}%`}
        </Text>
      </Col>
    </Cols>
  );
};

interface ICustomTooltipProps extends TooltipProps {
  metricsConfig: Partial<Record<IDashboardHomeCardMetricKey, IDashboardCard>>;
  grouping: number;
  currencySymbol: string;
}

const CustomTooltip = ({
  active,
  payload,
  metricsConfig,
  grouping,
  currencySymbol,
}: ICustomTooltipProps) => {
  if (active && !isEmpty(payload)) {
    const groupedPayloads = groupBy(payload, 'name');

    return (
      <Rows
        style={{
          backgroundColor: publicaColors.white,
          border: `1px solid ${publicaColors.lightGray}`,
          padding: spacing.small,
        }}
      >
        {Object.entries(groupedPayloads).map((array) => {
          const [label, payload] = array;
          let orderedPayload = payload;
          const metricInfo = Object.values(metricsConfig).find((metric) => label === metric.label);
          // in order to prioritize the line chart, we have to flip the values on the tooltip to move the main date up
          if (metricInfo?.chartType === 'line') {
            orderedPayload = payload.reverse();
          }
          let trendValue = 0;
          let isInverseTrend = false;
          if (orderedPayload.length === 2) {
            isInverseTrend = !!metricInfo?.isInverseTrend;
            const [mainValue, compareValue] = orderedPayload.map((item: any) => item.value);
            trendValue = calculateTrend(mainValue, compareValue);
          }
          return (
            <Rows paddingBottom={spacing.small}>
              <Row
                noPadding
                marginBottom={spacing.small}
                style={{
                  borderBottom: `1px solid ${publicaColors.lightGray}`,
                }}
              >
                <Cols
                  gap={spacing.large}
                  marginBottom={spacing.extraSmall}
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Col>
                    <Text small bold>
                      {label}
                    </Text>
                  </Col>
                  <Col>
                    <TrendDisplay value={trendValue} isInverseTrend={isInverseTrend} />
                  </Col>
                </Cols>
              </Row>
              {orderedPayload.map((item) => {
                const keys = (item.dataKey as string).split('.');
                const [range, metricKey] = keys;
                const payload = item.payload as Record<string, Record<string, unknown>>;
                const date = formatTooltipDate(payload[range].Date as string, grouping);
                const metric = metricsConfig[metricKey as IDashboardHomeCardMetricKey];
                const metricValue = Number(payload[range][metricKey]);
                const value = !isEmpty(metric)
                  ? formatTooltipValue(
                      isNaN(metricValue) ? null : metricValue,
                      metric.valueType,
                      currencySymbol,
                    )
                  : 'Metric Undefined';
                return (
                  <Row noPadding>
                    <Cols
                      gap={spacing.extraLarge}
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Col>
                        <Cols gap='4px' style={{ alignItems: 'center' }}>
                          <Col paddingBottom='1px'>
                            <Text color={item.fill} style={{ fontSize: fontSizing.mini }}>
                              ⬤
                            </Text>
                          </Col>
                          <Col>
                            <Text small bold>
                              {date}
                            </Text>
                          </Col>
                        </Cols>
                      </Col>
                      <Col>
                        <Text small>{value}</Text>
                      </Col>
                    </Cols>
                  </Row>
                );
              })}
            </Rows>
          );
        })}
      </Rows>
    );
  }
  return null;
};

interface IMobileCompareChartProps {
  isCompareEnabled?: boolean;
  loadState: LoadingState;
  metrics: IDashboardChartMetric[];
  data: Array<Record<string, any>>;
  metricsConfig: Partial<Record<IDashboardHomeCardMetricKey, IDashboardCard>>;
  grouping: number;
}

export const MobileCompareChart = ({
  isCompareEnabled,
  data = [],
  metrics,
  metricsConfig,
  grouping,
  loadState,
}: IMobileCompareChartProps) => {
  const { totalWidth } = useMobileState();
  const { currencySymbol } = useMobileStore();

  const formatTimestamp = (date: string | moment.Moment): string => {
    if (!date) {
      return '';
    }
    return moment(date)
      .utc()
      .format(grouping ? 'M/D' : 'HH:mm');
  };

  return (
    <div
      style={{
        width: '100%',
        height: '280px',
      }}
    >
      {loadState === LoadingStateLoading ? (
        <LoadingChart />
      ) : (
        <ComposedChart
          width={(totalWidth || 0) - 32}
          height={280}
          data={data}
          barGap={4}
          margin={{
            top: 0,
            right: 24,
            left: 0,
            bottom: 0,
          }}
        >
          <Tooltip
            content={
              // this bypasses an issue with automated props from recharts
              // @ts-ignore
              <CustomTooltip
                metricsConfig={metricsConfig}
                grouping={grouping}
                currencySymbol={currencySymbol}
              />
            }
            cursor={{ fill: publicaColors.lightGray }}
          />
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          {renderItems(metrics, data.length, isCompareEnabled)}
          <XAxis
            dataKey='main.Date'
            tick={{
              fontSize: 12,
              fill: 'black',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            // @ts-ignore
            tickFormatter={formatTimestamp}
            ticks={generateCompareChartTickArray(data)}
            domain={[0, data.length]}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{ fontSize: 12, fill: 'black', textAnchor: 'end' }}
            tickFormatter={convertLargeNumbersToRoundedString}
          />
        </ComposedChart>
      )}
    </div>
  );
};
